import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { useTextDomainContext, useUser } from "@/app/providers";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "@mui/material";
import { useCompanies, useTenantList } from "@/hooks";
import { getRoleTypeNameFromUser } from "@/utils";
import { CountryTimezoneModal } from "@/features";

const expireDaysLimit = 7;

export const TopNotifications = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const currentTenantId = user?.currentTenantId || "";
  const email = user?.email || "";
  const userId = user?.id || "";
  const roleType = (user && getRoleTypeNameFromUser(user)) || "company";

  const { tenantsList, loading: loadingTenants } = useTenantList({
    ignoreRole: true,
  });
  const firstTenantId = tenantsList?.[0]?.Id || "";
  const customerVariables = useMemo(
    () => ({
      tenantId: currentTenantId || firstTenantId || "--none--",
    }),
    [currentTenantId, firstTenantId],
  );

  const { customers, loading: loadingCustomers } =
    useCompanies(customerVariables);

  const [showDataHubInfo, setShowDataHubInfo] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [openTimezoneModal, setOpenTimezoneModal] = useState(false);
  const [showPartnerInfo, setShowPartnerInfo] = useState(false);
  const [showSubscriptionAlert, setShowSubscriptionAlert] = useState(true);

  // Dismissal tracking so closed modals and alerts do not re-open automatically
  const [dismissedModal, setDismissedModal] = useState(false);
  const [dismissedCountryAlert, setDismissedCountryAlert] = useState(false);
  const [dismissedPartnerInfo, setDismissedPartnerInfo] = useState(false);
  const [dismissedDataHubInfo, setDismissedDataHubInfo] = useState(false);

  // Reset all notification and dismissal states whenever user/userId changes
  useEffect(() => {
    setShowDataHubInfo(false);
    setShowCountryModal(false);
    setOpenTimezoneModal(false);
    setShowPartnerInfo(false);
    setShowSubscriptionAlert(true);
    setDismissedModal(false);
    setDismissedCountryAlert(false);
    setDismissedPartnerInfo(false);
    setDismissedDataHubInfo(false);
  }, [userId]);

  const customerKey = userId;

  // Calculate expireDays using useMemo
  const expireDays = useMemo(() => {
    if (!user || roleType !== "company") return 10000;
    const firstTenant = tenantsList?.[0];
    if (firstTenant?.subscriptionExpireAt) {
      const now = new Date();
      const expireAt = new Date(
        firstTenant.subscriptionExpireAt as string | number | Date,
      );
      const diffInMs = expireAt.getTime() - now.getTime();
      return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    }
    return 10000;
  }, [roleType, tenantsList, user]);

  // DataHub info notification (company with DK country and 0 companies)
  useEffect(() => {
    if (!user || roleType !== "company" || user?.countryCode !== "DK") {
      setShowDataHubInfo(false);
      return;
    }

    if (customers?.length) {
      setShowDataHubInfo(false);
    } else {
      if (!loadingCustomers && !dismissedDataHubInfo) {
        setShowDataHubInfo(true);
      }
    }
  }, [
    customers?.length,
    dismissedDataHubInfo,
    loadingCustomers,
    roleType,
    user,
    user?.countryCode,
  ]);

  const isNameMissing = !user?.name?.trim();
  const isCountryMissing = !user?.countryCode;
  const hasTenant =
    Boolean(tenantsList?.length) ||
    Boolean(currentTenantId && currentTenantId !== "none");
  const isTenantMissing = !hasTenant;
  const isProfileComplete =
    !isNameMissing && !isCountryMissing && !isTenantMissing;

  // Account Setup modal & alert (company with missing name, country, or 0 tenants)
  useEffect(() => {
    if (!user || roleType !== "company") {
      setShowCountryModal(false);
      setOpenTimezoneModal(false);
      return;
    }

    if (isProfileComplete) {
      setShowCountryModal(false);
      setOpenTimezoneModal(false);
    } else {
      if (!loadingTenants) {
        if (!dismissedCountryAlert) {
          setShowCountryModal(true);
        }
        if (!dismissedModal) {
          setOpenTimezoneModal(true);
        }
      }
    }
  }, [
    dismissedCountryAlert,
    dismissedModal,
    isProfileComplete,
    loadingTenants,
    roleType,
    user,
  ]);

  // Partner info alert (partner with 0 tenants)
  useEffect(() => {
    if (!user || roleType !== "partner") {
      setShowPartnerInfo(false);
      return;
    }

    if (tenantsList?.length) {
      setShowPartnerInfo(false);
    } else {
      if (!loadingTenants && !dismissedPartnerInfo) {
        setShowPartnerInfo(true);
      }
    }
  }, [
    dismissedPartnerInfo,
    loadingTenants,
    roleType,
    tenantsList?.length,
    user,
  ]);

  const handlePartnerInfoClose = () => {
    setShowPartnerInfo(false);
    setDismissedPartnerInfo(true);
  };

  const handleDataHubAlertClose = () => {
    setShowDataHubInfo(false);
    setDismissedDataHubInfo(true);
  };

  const handleCountryAlertClose = () => {
    setShowCountryModal(false);
    setDismissedCountryAlert(true);
  };

  const handleCountryLinkClick = () => {
    setDismissedModal(false);
    setOpenTimezoneModal(true);
  };

  const handleTimezoneModalClose = () => {
    setOpenTimezoneModal(false);
    setDismissedModal(true);
  };

  const alertTitle = useMemo(() => {
    if (isNameMissing && !isCountryMissing && !isTenantMissing) {
      return gettext("Full Name is Required!");
    }
    if (!isNameMissing && isCountryMissing && !isTenantMissing) {
      return gettext("Country is Required!");
    }
    if (!isNameMissing && !isCountryMissing && isTenantMissing) {
      return gettext("Corporate Group is Required!");
    }
    return gettext("Account Setup Required!");
  }, [gettext, isCountryMissing, isNameMissing, isTenantMissing]);

  const alertContent = useMemo(() => {
    if (isNameMissing && isCountryMissing && isTenantMissing) {
      return (
        <>
          {gettext("To get started, please provide your")}{" "}
          <Link
            onClick={handleCountryLinkClick}
            sx={{ cursor: "pointer", fontWeight: 600 }}
          >
            {gettext("Full Name, Country, and Corporate Group")}
          </Link>
          .
        </>
      );
    }
    if (isNameMissing && isTenantMissing) {
      return (
        <>
          {gettext("To get started, please provide your")}{" "}
          <Link
            onClick={handleCountryLinkClick}
            sx={{ cursor: "pointer", fontWeight: 600 }}
          >
            {gettext("Full Name and Corporate Group")}
          </Link>
          .
        </>
      );
    }
    if (isCountryMissing && isTenantMissing) {
      return (
        <>
          {gettext("To get started, please provide your")}{" "}
          <Link
            onClick={handleCountryLinkClick}
            sx={{ cursor: "pointer", fontWeight: 600 }}
          >
            {gettext("Country and Corporate Group")}
          </Link>
          .
        </>
      );
    }
    if (isNameMissing && isCountryMissing) {
      return (
        <>
          {gettext("To get started, please provide your")}{" "}
          <Link
            onClick={handleCountryLinkClick}
            sx={{ cursor: "pointer", fontWeight: 600 }}
          >
            {gettext("Full Name and Country")}
          </Link>
          .
        </>
      );
    }
    if (isTenantMissing) {
      return (
        <>
          {gettext("To manage your data, we require you to create your")}{" "}
          <Link
            onClick={handleCountryLinkClick}
            sx={{ cursor: "pointer", fontWeight: 600 }}
          >
            {gettext("Corporate Group / Company Name")}
          </Link>
          .
        </>
      );
    }
    if (isCountryMissing) {
      return (
        <>
          {gettext("To manage your data, please select your")}{" "}
          <Link
            onClick={handleCountryLinkClick}
            sx={{ cursor: "pointer", fontWeight: 600 }}
          >
            {gettext("Country")}
          </Link>
          .
        </>
      );
    }
    if (isNameMissing) {
      return (
        <>
          {gettext("To personalize your account, please enter your")}{" "}
          <Link
            onClick={handleCountryLinkClick}
            sx={{ cursor: "pointer", fontWeight: 600 }}
          >
            {gettext("Full Name")}
          </Link>
          .
        </>
      );
    }
    return null;
  }, [gettext, isCountryMissing, isNameMissing, isTenantMissing]);

  // If user is not authenticated, do not render any notifications or modals
  if (!user) {
    return null;
  }

  return (
    <>
      {showPartnerInfo && (
        <Alert severity="info" onClose={handlePartnerInfoClose}>
          <AlertTitle>{gettext("Information!")}</AlertTitle>
          {gettext(
            "No company is currently associated with your partner account. As a result, some features are unavailable. Please contact us if you have any questions or need assistance.",
          )}
          &nbsp;
        </Alert>
      )}

      {showDataHubInfo && (
        <Alert severity="info" onClose={handleDataHubAlertClose}>
          <AlertTitle>{gettext("Access is Required!")}</AlertTitle>
          {gettext("Please authorize us on")}&nbsp;
          <Link href={`/r/fb/${customerKey}/${email}`} target="_blank">
            {gettext("DataHub")}&nbsp;
          </Link>
          {gettext("or")}&nbsp;
          {gettext("contact us")}&nbsp;
          {gettext("for any queries.")}&nbsp;
        </Alert>
      )}

      {showSubscriptionAlert &&
        expireDays < expireDaysLimit &&
        expireDays > 0 && (
          <Alert
            severity="info"
            onClose={() => setShowSubscriptionAlert(false)}
          >
            <AlertTitle>{gettext("Subscription Expiring Soon!")}</AlertTitle>
            {gettext("Your subscription will expire in")}&nbsp;
            {expireDays - 1}
            {expireDays > 2 &&
              gettext(
                " days. Renew now to continue enjoying uninterrupted access.",
              )}
            {expireDays < 3 &&
              gettext(
                " day. Renew now to continue enjoying uninterrupted access.",
              )}
          </Alert>
        )}

      {showSubscriptionAlert && expireDays <= 0 && (
        <Alert severity="info" onClose={() => setShowSubscriptionAlert(false)}>
          <AlertTitle>{gettext("Subscription Expired!")}</AlertTitle>
          {gettext(
            "Your subscription has expired. Renew now to regain access to your account and all premium features.",
          )}
        </Alert>
      )}

      {showCountryModal && (
        <Alert severity="info" onClose={handleCountryAlertClose}>
          <AlertTitle>{alertTitle}</AlertTitle>
          {alertContent}
        </Alert>
      )}

      <CountryTimezoneModal
        open={openTimezoneModal}
        handleClose={handleTimezoneModalClose}
      />
    </>
  );
};
